import React, { useContext } from 'react';
import { useSelector } from 'react-redux';
import { skipToken } from '@reduxjs/toolkit/query';
import _ from 'lodash';
import {
  SBadge,
  SIcon,
  SSpinner,
  STable,
  STooltip,
  type SIconName,
} from '@chargebee/sting-react';
import {
  PlayContext,
  PlayObjectProperty,
} from '@bbkAdminComponents/play/play-context';
import { DatePickerV2 } from '@bbkAdminComponents/reports-filter-picker/reports-filter-picker';
import { selectAppId } from '@bbkAdminRedux/app/selectors';
import { selectFromResultTyped } from '@bbkAdminRedux/redux_utils';
import { useGetSelectedBrand } from '@bbkAdminRedux/rtkq/brands.slice';
import { useSelectDatePicker } from '@bbkAdminRedux/rtkq/datepicker-persist.slice';
import { pricingPagesSlice } from '@bbkAdminRedux/rtkq/pricing-pages.slice';
import {
  reportsSlice,
  ReportType,
  type PricingPageReportByPlay,
} from '@bbkAdminRedux/rtkq/reports.slice';

type Props = {
  playId: string;
};

type ColumnField = {
  name: string;
  fieldName: string;
  description: string;
  percentage: boolean;
};

type ColumnFieldsItem = {
  label: string;
  description: string;
  icon: SIconName;
  fields: ColumnField[];
};

const columnFields: ColumnFieldsItem[] = [
  {
    label: 'User Report',
    description:
      'User level conversion data for the pricing tables in the play.',
    icon: 'shield-user',
    fields: [
      {
        name: 'customers_entered',
        fieldName: 'Customers Entered',
        description: 'Number of customers who entered the pricing page',
        percentage: false,
      },
      {
        name: 'customers_engaged',
        fieldName: 'Customers Engaged',
        description: 'Number of customers who engaged with the pricing page',
        percentage: false,
      },
      {
        name: 'click_through_rate',
        fieldName: 'Click Through Rate',
        description: 'Percentage of customers who clicked through',
        percentage: true,
      },
      {
        name: 'checkouts_started',
        fieldName: 'Checkouts Started',
        description: 'Number of customers who started checkout',
        percentage: false,
      },
      {
        name: 'checkouts_completed',
        fieldName: 'Checkouts Completed',
        description: 'Number of customers who completed checkout',
        percentage: false,
      },
      {
        name: 'checkout_rate',
        fieldName: 'Checkout Rate',
        description: 'Percentage of customers who completed checkout',
        percentage: true,
      },
      {
        name: 'overall_conversion_rate',
        fieldName: 'Overall Conversion Rate',
        description: 'Overall conversion rate for the pricing page',
        percentage: true,
      },
    ],
  },
  {
    label: 'Session Report',
    description:
      'Session level conversion data for the pricing tables in the play. (A user can create multiple sessions within the applied time period.)',
    icon: 'shopping-cart',
    fields: [
      {
        name: 'sessions_rendered',
        fieldName: 'Sessions Rendered',
        description: 'Number of pricing page sessions rendered',
        percentage: false,
      },
      {
        name: 'sessions_engaged',
        fieldName: 'Sessions w/ engagement',
        description: 'Number of pricing page sessions with engagement',
        percentage: false,
      },
      {
        name: 'click_through_rate',
        fieldName: 'Click Through Rate',
        description: 'Percentage of sessions with click through',
        percentage: true,
      },
      {
        name: 'sessions_checkouts_started',
        fieldName: 'Sessions Checkouts Started',
        description: 'Number of sessions where checkout was started',
        percentage: false,
      },
      {
        name: 'sessions_checkouts_completed',
        fieldName: 'Sessions Checkouts Completed',
        description: 'Number of sessions where checkout was completed',
        percentage: false,
      },
      {
        name: 'checkout_rate',
        fieldName: 'Checkout Rate',
        description: 'Percentage of sessions with completed checkout',
        percentage: true,
      },
      {
        name: 'overall_conversion_rate',
        fieldName: 'Overall Conversion Rate',
        description: 'Overall conversion rate for sessions',
        percentage: true,
      },
    ],
  },
];

const PlayPricingTableReports: React.FC<Props> = ({ playId }) => {
  // hooks
  const appKey = useSelector(selectAppId);
  const brandId = useGetSelectedBrand(appKey);
  const { getPlayObjectProperty } = useContext(PlayContext);
  const { startDate: start, endDate: end } = useSelectDatePicker();

  // slices
  const {
    data,
    isLoading: isReportLoading,
    isFetching: isReportFetching,
  } = reportsSlice.endpoints.getReportV2.useQuery(
    {
      appKey: appKey,
      start,
      end,
      report_type_requests: [
        { report_type: ReportType.pricing_page_report_by_play },
      ],
      context: undefined,
    },
    {
      skip: !appKey,
      selectFromResult: selectFromResultTyped<PricingPageReportByPlay>,
    }
  );

  const { data: pricingPagesList = [], isLoading: isLoadingPricingPages } =
    pricingPagesSlice.endpoints.getPricingPagesList.useQuery(
      appKey ? { appKey, brand_id: brandId } : skipToken
    );

  // variables
  const playReportData = React.useMemo(() => {
    return data?.data.filter((reportData) => {
      return reportData.play_id === playId;
    });
  }, [data, playId]);

  const liveControl = React.useMemo(() => {
    return (
      getPlayObjectProperty(PlayObjectProperty.LIVE_CONTROL_NODE)?.target
        .target_key ?? undefined
    );
  }, [playReportData]);

  const liveControlData = playReportData?.find(
    (report) => report.target_id === liveControl
  );

  const renderTableComponent = (column: ColumnFieldsItem) => {
    return (
      <div className="tw-mb-8 tw-w-full tw-flex-col">
        <div className="tw-mb-4 tw-flex tw-items-center tw-gap-2">
          <div className="tw-flex tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-primary-100 tw-bg-primary-50 tw-p-2">
            <SIcon
              name={column.icon}
              size={18}
              className="tw-text-primary-600"
            />
          </div>
          <span className="tw-text-lg tw-font-semibold tw-text-neutral-900">
            {column.label}
          </span>
        </div>
        <div className="tw-mb-4 tw-text-sm tw-font-light tw-text-neutral-600">
          {column.description}
        </div>
        <STable>
          <STable.Head>
            <STable.Row>
              <STable.HeaderCell className="!tw-text-xs">
                Pricing table
              </STable.HeaderCell>
              {column.fields.map((field) => (
                <STable.HeaderCell key={field.name} className="!tw-text-xs">
                  <div className="tw-flex tw-items-center tw-justify-center">
                    <span className="tw-whitespace-normal tw-break-words tw-text-center">
                      {field.fieldName}
                    </span>
                    {field.description ? (
                      <STooltip
                        label={field.description}
                        width="regular"
                        placement="top"
                      >
                        <SIcon
                          name="circle-question-mark"
                          size={14}
                          className="tw-ml-0.5 tw-flex-shrink-0 tw-text-neutral-400"
                        />
                      </STooltip>
                    ) : null}
                  </div>
                </STable.HeaderCell>
              ))}
              <STable.HeaderCell />
            </STable.Row>
          </STable.Head>
          <STable.Body>
            {liveControl && liveControl != 'none' && (
              <STable.Row key={liveControl}>
                <STable.Cell>
                  <div className="tw-flex tw-items-center tw-gap-2">
                    <span>
                      {pricingPagesList.find((page) => page.id === liveControl)
                        ?.name || 'Control'}
                    </span>
                    <SBadge
                      mode="dark"
                      size="regular"
                      className="!tw-px-2"
                      variant="success"
                    >
                      Control
                    </SBadge>
                  </div>
                </STable.Cell>
                {column.fields.map((field) => (
                  <STable.Cell key={field.name} className="tw-text-center">
                    {_.get(liveControlData, field.name, 0)}
                    {field.percentage ? '%' : ''}
                  </STable.Cell>
                ))}
                <STable.Cell />
              </STable.Row>
            )}
            {playReportData
              ?.filter((report) => report.target_id !== liveControl)
              .map((report) => (
                <STable.Row key={report.target_id}>
                  <STable.Cell>
                    {pricingPagesList.find(
                      (page) => page.id === report.target_id
                    )?.name || 'N/A'}
                  </STable.Cell>
                  {column.fields.map((field) => (
                    <STable.Cell key={field.name} className="tw-text-center">
                      {_.get(report, field.name, 0)}
                      {field.percentage ? '%' : ''}
                    </STable.Cell>
                  ))}
                  <STable.Cell />
                </STable.Row>
              ))}
          </STable.Body>
        </STable>
      </div>
    );
  };

  return (
    <div className="tw-flex tw-w-full tw-flex-col tw-gap-6 tw-px-4">
      <div className="tw-flex tw-w-full tw-justify-end">
        <DatePickerV2 />
      </div>
      {isReportLoading || isLoadingPricingPages || isReportFetching ? (
        <div className="tw-flex tw-min-h-[80vh] tw-w-full tw-flex-1 tw-flex-col tw-items-center tw-justify-center">
          <SSpinner
            size="medium"
            testId="default-spinner"
            className="!tw-text-white"
          />
        </div>
      ) : playReportData && playReportData.length > 0 ? (
        <>
          {columnFields.map((column) => (
            <div key={column.label}>{renderTableComponent(column)}</div>
          ))}
        </>
      ) : (
        <div className="tw-flex tw-w-full tw-items-center tw-justify-center tw-pt-8">
          <span className="tw-text-neutral-600">No session data found</span>
        </div>
      )}
    </div>
  );
};

export default PlayPricingTableReports;
